/**
 * Product Builder — Design Library field runtime (live App Proxy + mock fallback).
 */
(function (global) {
  'use strict';

  var logic = global.PBDesignLibraryLogic || {};
  var parseDesignLibrarySettings = logic.parseDesignLibrarySettings;
  var buildOrderProps = logic.buildOrderProps;
  var buildLookupRequest = logic.buildLookupRequest;
  var resolveProductionPageLabels = logic.resolveProductionPageLabels;
  var decideStepVisibility = logic.decideStepVisibility;
  var isScratchOnlyResponse = logic.isScratchOnlyResponse;
  var shouldShowNewDesign = logic.shouldShowNewDesign;
  var shouldShowCatalogPicker = logic.shouldShowCatalogPicker;
  var applyDesignLibraryDisplayCap = logic.applyDesignLibraryDisplayCap;
  var selectOtherShapeOptions = logic.selectOtherShapeOptions;
  var shapeMatchesOptionValue = logic.shapeMatchesOptionValue;
  var designLibraryCacheKey = logic.designLibraryCacheKey;
  var reshapeResponseForShape = logic.reshapeResponseForShape;
  var resolveSelectionForShape = logic.resolveSelectionForShape;
  var summariesForChosenShape = logic.summariesForChosenShape;
  var formatDesignMatch = logic.formatDesignMatch;
  var knownDesignShape = logic.knownDesignShape;
  var mergeDesignMatch = logic.mergeDesignMatch;
  var CART_KEY_MATCH = logic.CART_KEY_MATCH || '_design_match';
  var CART_KEY_ENTRY_ID = logic.CART_KEY_ENTRY_ID || '_design_library_entry_id';
  var shouldAutoSwitchCartEditOnDepsChange = logic.shouldAutoSwitchCartEditOnDepsChange;
  var shouldPreserveCartEditSelectionOnLookup = logic.shouldPreserveCartEditSelectionOnLookup;
  var resolveNewDesignLabelForRestore = logic.resolveNewDesignLabelForRestore;
  var mockResolveDesignLibrary = logic.mockResolveDesignLibrary;
  var resolveDesignLibrary = logic.resolveDesignLibrary;
  var resolveDesignLibraryWithRetry = logic.resolveDesignLibraryWithRetry;
  var shouldUseMockDesignLibrary = logic.shouldUseMockDesignLibrary;
  var normalizeDesignLibraryLayout = logic.normalizeDesignLibraryLayout;
  var renderThumbnailGrid = logic.renderThumbnailGrid;
  var getFirstMatchSummary = logic.getFirstMatchSummary;
  var orderPropsKey = logic.orderPropsKey;
  var findMatchSummary = logic.findMatchSummary;
  var resolveCatalogEntryFromSnapshot = logic.resolveCatalogEntryFromSnapshot;
  var selectDesignCardInGrid = logic.selectDesignCardInGrid;
  var clearDesignLibraryCartProps = logic.clearDesignLibraryCartProps;
  var buildCartPropsFromSelection = logic.buildCartPropsFromSelection;
  var isDesignLibraryStepComplete = logic.isDesignLibraryStepComplete;
  var parseDesignLibraryCartSnapshot = logic.parseDesignLibraryCartSnapshot;
  var beginLookupGeneration = logic.beginLookupGeneration;
  var cancelInFlightLookup = logic.cancelInFlightLookup;
  var isExpectedLookupAbort = logic.isExpectedLookupAbort;
  var isStaleLookupGeneration = logic.isStaleLookupGeneration;
  var shouldScheduleDesignLibraryLookup = logic.shouldScheduleDesignLibraryLookup;
  var shouldScheduleDesignLibraryPrefetch = logic.shouldScheduleDesignLibraryPrefetch;
  var hasCachedLookupResult = logic.hasCachedLookupResult;
  var DEBOUNCE_MS = logic.DEBOUNCE_MS || 400;
  var LOOKUP_MAX_RETRIES = typeof logic.LOOKUP_MAX_RETRIES === 'number' ? logic.LOOKUP_MAX_RETRIES : 2;
  var DEFAULT_NEW_DESIGN_LABEL = logic.DEFAULT_NEW_DESIGN_LABEL || 'Create from scratch';
  var DEFAULT_NEW_DESIGN_PREVIEW = logic.DEFAULT_NEW_DESIGN_PREVIEW || 'Original design';
  var DEFAULT_NEW_DESIGN_PREVIEW_MSG = logic.DEFAULT_NEW_DESIGN_PREVIEW_MSG || 'Your item will be created from scratch.';
  var resolveNewDesignPreviewCopy = logic.resolveNewDesignPreviewCopy;

  function ensureCatalogState() {
    var s = global.builderState;
    if (!s) return null;
    /* Row-scoped during bundle SUB edit — root catalog stays main-owned (PB-FND-64). */
    if (s.mode === 'SUB' && s.sub && s.sub.isQueueItem) {
      if (!s.sub.catalogDesignLibrary) s.sub.catalogDesignLibrary = { byLabel: {} };
      if (!s.sub.catalogDesignLibrary.byLabel) s.sub.catalogDesignLibrary.byLabel = {};
      return s.sub.catalogDesignLibrary;
    }
    if (!s.catalogDesignLibrary) s.catalogDesignLibrary = { byLabel: {} };
    if (!s.catalogDesignLibrary.byLabel) s.catalogDesignLibrary.byLabel = {};
    return s.catalogDesignLibrary;
  }

  /** The wrap for this label in the flow that is active NOW — a bundle row's, never the main
   *  flow's by accident (the first match in the document is always the main flow's). */
  function findWrapForLabel(label) {
    if (typeof document === 'undefined') return null;
    var parts = getContainerParts();
    var scoped = parts
      ? document.querySelector(parts.containerID + ' .pb-dl-wrap[data-pb-dl-label="' + label + '"]')
      : null;
    return scoped || document.querySelector('.pb-dl-wrap[data-pb-dl-label="' + label + '"]');
  }

  function getFieldState(label) {
    var catalog = ensureCatalogState();
    if (!catalog) return {};
    if (!catalog.byLabel[label]) {
      catalog.byLabel[label] = {
        loading: false,
        skipped: false,
        shown: false,
        response: null,
        debounceTimer: null,
        orderPropsKey: '',
        lookupGeneration: 0,
        abortController: null,
        lookupPromise: null,
        selectedEntryId: null,
        selectionMode: null,
        previewUrl: '',
        productionUrl: '',
        designMatch: '',
        selectionOrigin: null,
        pendingCartRestore: null,
        prefetchedReady: false,
        cartEditDepsChanged: false,
        restoredCartLabel: null
      };
    }
    return catalog.byLabel[label];
  }

  function resetFieldState(label) {
    var state = getFieldState(label);
    if (state.debounceTimer) {
      clearTimeout(state.debounceTimer);
      state.debounceTimer = null;
    }
    state.loading = false;
    state.skipped = false;
    state.shown = false;
    state.response = null;
    state.orderPropsKey = '';
    if (cancelInFlightLookup) {
      cancelInFlightLookup(state, 'reset');
    }
    state.selectedEntryId = null;
    state.selectionMode = null;
    state.previewUrl = '';
    state.productionUrl = '';
    state.designMatch = '';
    state.selectionOrigin = null;
    state.pendingShapeSwitch = null;
    state.pendingCartRestore = null;
    state.prefetchedReady = false;
    state.restoredCartLabel = null;
    clearCartPropsForLabel(label);
  }

  function isCartEditSession() {
    return !!(global.builderState && global.builderState.cartEdit);
  }

  /**
   * MNGOPT-10 — the cache key for this field's current inputs, and the shape to read it as.
   *
   * These are two different things now. The key says WHICH lookup answers the step; the shape says
   * how to read that one reply. Keeping them separate is what lets a shape change be instant.
   */
  function lookupIdentity(settings, orderProps) {
    var shapeField = settings.shape_field || 'Shape';
    var chosenShape = '';
    Object.keys(orderProps || {}).forEach(function (k) {
      if (String(k).trim().toLowerCase() === String(shapeField).trim().toLowerCase()) {
        chosenShape = orderProps[k];
      }
    });
    return {
      key: designLibraryCacheKey ? designLibraryCacheKey(orderProps, settings) : orderPropsKey(orderProps),
      shape: chosenShape
    };
  }

  /** The shape the shopper currently has selected, per this field's `shape_field`. */
  function currentChosenShape(settings) {
    var ctx = getActiveContext();
    var gate = buildOrderProps
      ? buildOrderProps(settings.depends_on, ctx && ctx.selections, settings.default_order_props)
      : null;
    if (!gate || !gate.complete) return '';
    return lookupIdentity(settings, gate.orderProps).shape;
  }

  function getWrapSettings(wrap) {
    return parseDesignLibrarySettings
      ? parseDesignLibrarySettings((wrap && wrap.dataset && wrap.dataset.settings) || '')
      : {};
  }

  function clearDesignSelectionFromContext(label) {
    var selections = getSelectionsTarget();
    if (selections && clearDesignLibraryCartProps) clearDesignLibraryCartProps(selections, label);
    var state = getFieldState(label);
    state.pendingCartRestore = null;
    state.restoredCartLabel = null;
  }

  function summaryFromCatalogPick(state, card) {
    if (!card) return null;
    var entryId = card.dataset.entryId || card.getAttribute('data-entry-id') || null;
    var summary = entryId && state && state.response
      ? findMatchSummary(state.response, entryId)
      : null;
    if (summary) return summary;
    return {
      entryId: entryId,
      previewUrl: card.dataset.previewUrl || card.getAttribute('data-preview-url') || '',
      productionUrl: card.dataset.productionUrl || card.getAttribute('data-production-url') || '',
      label: card.dataset.label || ''
    };
  }

  function commitCatalogUrlsToFieldState(state, summary) {
    if (!state) return;
    state.previewUrl = summary && summary.previewUrl ? String(summary.previewUrl) : '';
    state.productionUrl = summary && summary.productionUrl ? String(summary.productionUrl) : '';
  }

  function parseCartSnapshotOptions(wrap) {
    var label = (wrap && wrap.dataset && wrap.dataset.pbDlLabel) || '';
    var settings = wrap && parseDesignLibrarySettings
      ? parseDesignLibrarySettings(wrap.dataset.settings || '')
      : { new_design_label: DEFAULT_NEW_DESIGN_LABEL };
    var newDesignLabel = settings.new_design_label || DEFAULT_NEW_DESIGN_LABEL;
    return {
      fieldLabels: label ? [label] : [],
      newDesignLabels: label ? { [label]: newDesignLabel } : {},
      defaultFieldLabel: label || 'Design'
    };
  }

  function parseCartSnapshotFromProps(props, wrap) {
    if (!parseDesignLibraryCartSnapshot || !props) return null;
    var options = wrap ? parseCartSnapshotOptions(wrap) : { defaultFieldLabel: 'Design' };
    return parseDesignLibraryCartSnapshot(props, options);
  }

  function queueCartEditRestore(wrap) {
    var edit = global.builderState && global.builderState.cartEdit;
    if (!edit || !edit.preservedProps) return;
    var label = wrap.dataset.pbDlLabel || '';
    var snapshot = parseCartSnapshotFromProps(edit.preservedProps, wrap);
    if (!snapshot || !snapshot.mode) return;
    getFieldState(label).pendingCartRestore = snapshot;
  }

  function restoreCartEditSelection(wrap, label, snapshot) {
    if (!snapshot || !snapshot.mode) return;
    var state = getFieldState(label);
    var grid = wrap.querySelector('.pb-dl-grid');
    var index = (global.BUILDER_DATA && global.BUILDER_DATA.designLibraryIndex) || [];
    var resolved = resolveCatalogEntryFromSnapshot
      ? resolveCatalogEntryFromSnapshot(snapshot, state.response, index)
      : snapshot;

    if (resolved.mode === 'new_design') {
      var settings = getWrapSettings(wrap);
      var newDesignLabel = resolveNewDesignLabelForRestore
        ? resolveNewDesignLabelForRestore(snapshot, settings)
        : (settings.new_design_label || DEFAULT_NEW_DESIGN_LABEL);
      state.selectedEntryId = null;
      state.selectionMode = 'new_design';
      state.restoredCartLabel = snapshot.savedLabelText || newDesignLabel;
      setScratchSelected(wrap, true);
      if (grid) grid.querySelectorAll('.pb-design-card').forEach(function (el) { el.classList.remove('selected'); });
      setWrapUi(wrap, { requiredValid: true });
      updatePreviewForWrap(wrap, '', newDesignLabel, { scratch: true });
      syncCartPropsForLabel(label);
      return;
    }

    if (resolved.mode === 'catalog') {
      var summary = resolved.entryId && state.response
        ? findMatchSummary(state.response, resolved.entryId)
        : null;
      if (!summary && resolved.entryId) {
        summary = {
          entryId: resolved.entryId,
          previewUrl: resolved.previewUrl,
          productionUrl: resolved.productionUrl
        };
      }
      if (!summary && resolved.previewUrl && logic.findMatchSummaryByPreviewUrl) {
        summary = logic.findMatchSummaryByPreviewUrl(state.response, resolved.previewUrl);
      }
      var previewUrl = summary ? (summary.previewUrl || resolved.previewUrl) : resolved.previewUrl;
      state.selectedEntryId = summary && summary.entryId != null
        ? summary.entryId
        : (resolved.entryId || null);
      state.selectionMode = 'catalog';
      commitCatalogUrlsToFieldState(state, summary || resolved);
      setScratchSelected(wrap, false);
      setWrapUi(wrap, { requiredValid: true });
      if (grid) {
        if (selectDesignCardInGrid) {
          selectDesignCardInGrid(grid, resolved, state.response);
        } else if (resolved.entryId) {
          var card = grid.querySelector('[data-entry-id="' + resolved.entryId + '"]');
          grid.querySelectorAll('.pb-design-card').forEach(function (el) { el.classList.remove('selected'); });
          if (card) card.classList.add('selected');
        }
      }
      updatePreviewForWrap(wrap, previewUrl || '', summary && summary.label ? summary.label : '');
      syncCartPropsForLabel(label);
    }
  }

  function maybeApplyCartEditRestore(wrap, label) {
    var state = getFieldState(label);
    if (state.cartEditDepsChanged) return;
    if (!state.pendingCartRestore || !state.shown) return;
    var snapshot = state.pendingCartRestore;
    state.pendingCartRestore = null;
    restoreCartEditSelection(wrap, label, snapshot);
    var PB = global.productBuilder;
    if (PB && typeof PB.checkStepValidity === 'function') PB.checkStepValidity();
  }

  function getSelectionsTarget() {
    var ctx = getActiveContext();
    return ctx && ctx.selections ? ctx.selections : null;
  }

  function clearCartPropsForLabel(label) {
    var selections = getSelectionsTarget();
    if (selections && clearDesignLibraryCartProps) clearDesignLibraryCartProps(selections, label);
  }

  /**
   * MNGSD-2 D4 — settle the selection against the shape the shopper has NOW.
   *
   * Re-derived from the reply already in memory: no request. A design the reply places on
   * another shape is followed to this shape's newest, or lands on Original Design. A design the
   * reply cannot place is left alone — unknown is not wrong. It can only correct; it cannot
   * refuse, and it cannot leave the line empty.
   *
   * When the step is on screen the change goes through the same selection code a tap uses, so
   * the card, the preview, the state and the cart line always agree. Skipped while an
   * other-shape tap or a cart-edit restore is in flight — except at the final flush, where the
   * line is about to be written and nothing is left to chance.
   */
  function settleSelectionForShape(label, settings, options) {
    options = options || {};
    var state = getFieldState(label);
    if (!settings || settings.show_other_shapes !== true) return false;
    var fallbackOriginal = state.selectionMode === 'new_design' && state.selectionOrigin === 'fallback';
    if ((state.selectionMode !== 'catalog' && !fallbackOriginal) || !state.response) return false;
    if (!options.final && (state.pendingCartRestore || state.pendingShapeSwitch)) return false;
    if (!resolveSelectionForShape || !summariesForChosenShape || !knownDesignShape) return false;
    var chosenShape = currentChosenShape(settings);
    if (!chosenShape) return false;
    var next = resolveSelectionForShape(
      { mode: state.selectionMode, entryId: state.selectedEntryId, origin: state.selectionOrigin },
      summariesForChosenShape(state.response, chosenShape, settings),
      { heldShape: knownDesignShape(state.response, state.selectedEntryId), chosenShape: chosenShape }
    );
    if (next.outcome !== 'followed' && next.outcome !== 'recovered' && next.outcome !== 'fallback') return false;
    var wrap = options.wrap || findWrapForLabel(label);
    var newDesignLabel = settings.new_design_label || DEFAULT_NEW_DESIGN_LABEL;
    if (next.outcome === 'followed' || next.outcome === 'recovered') {
      var summary = findMatchSummary(state.response, next.entryId);
      /* Never follow into nothing: a design the reply cannot describe is not one to sell. */
      if (!summary || !(summary.previewUrl || summary.productionUrl)) return false;
      var card = wrap
        ? wrap.querySelector('.pb-dl-grid .pb-design-card--catalog:not(.pb-design-card--other-shape)[data-entry-id="' + next.entryId + '"]')
        : null;
      if (card) {
        selectCatalogCard(wrap, card, label);
        return true;
      }
      state.selectedEntryId = String(summary.entryId);
      state.selectionMode = 'catalog';
      state.selectionOrigin = null;
      commitCatalogUrlsToFieldState(state, summary);
      return true;
    }
    if (!wrap) return false;
    selectScratchCard(wrap, label, newDesignLabel, { origin: 'fallback' });
    return true;
  }

  /**
   * MNGSD-2 D3 — the line says what its design IS: the name it was matched on and the design's
   * own shape, as the server stamped it. Read back by the intake net after payment.
   */
  function computeDesignMatch(state, settings, selections, summary) {
    if (!state || state.selectionMode !== 'catalog' || !settings || !buildOrderProps || !formatDesignMatch) return '';
    var gate = buildOrderProps(settings.depends_on, selections, settings.default_order_props);
    if (!gate.complete) return '';
    return formatDesignMatch(gate.orderProps, {
      shapeField: settings.shape_field,
      designShape: (knownDesignShape ? knownDesignShape(state.response, state.selectedEntryId) : '') ||
        (summary && summary.shape)
    });
  }

  function syncCartPropsForLabel(label, options) {
    options = options || {};
    if (!buildCartPropsFromSelection || !clearDesignLibraryCartProps) return;
    var selections = getSelectionsTarget();
    if (!selections) return;
    var state = getFieldState(label);
    /* MNGSD-2 D3 — what the line said before this rebuild, and for which design. A reload loses
       the reply, and a line rebuilt without it can only name the design; it must not replace a
       line that also knew the shape. */
    var priorRestore = state.pendingCartRestore;
    var priorMatch = selections[CART_KEY_MATCH] || state.designMatch ||
      (priorRestore && priorRestore.designMatch) || '';
    var priorEntryId = selections[CART_KEY_ENTRY_ID] != null
      ? String(selections[CART_KEY_ENTRY_ID])
      : (priorRestore && priorRestore.entryId != null ? String(priorRestore.entryId) : '');
    clearDesignLibraryCartProps(selections, label);
    if (state.skipped) return;
    if (!state.shown && !shouldPreserveSelection(state)) return;
    var newDesignLabel = DEFAULT_NEW_DESIGN_LABEL;
    var wrap = findWrapForLabel(label);
    var settings = null;
    if (wrap && parseDesignLibrarySettings) {
      settings = parseDesignLibrarySettings(wrap.dataset.settings || '');
      newDesignLabel = settings.new_design_label || DEFAULT_NEW_DESIGN_LABEL;
    }
    if (state.restoredCartLabel && state.selectionMode === 'new_design' && !state.cartEditDepsChanged) {
      newDesignLabel = state.restoredCartLabel;
    }
    var restore = state.pendingCartRestore;
    settleSelectionForShape(label, settings, { final: !!options.final, wrap: wrap });
    var fallbackSummary = null;
    if (state.selectionMode === 'catalog') {
      fallbackSummary = findMatchSummary(state.response, state.selectedEntryId);
      if (!fallbackSummary && (state.selectedEntryId || state.previewUrl || (restore && restore.previewUrl))) {
        fallbackSummary = {
          entryId: state.selectedEntryId || (restore && restore.entryId) || null,
          previewUrl: state.previewUrl || (restore && restore.previewUrl) || '',
          productionUrl: state.productionUrl || (restore && restore.productionUrl) || ''
        };
      }
    }
    var freshMatch = computeDesignMatch(state, settings, selections, fallbackSummary);
    state.designMatch = mergeDesignMatch && priorMatch &&
      state.selectionMode === 'catalog' && String(state.selectedEntryId || '') === priorEntryId
      ? mergeDesignMatch(priorMatch, freshMatch)
      : freshMatch;
    var props = buildCartPropsFromSelection({
      mode: state.selectionMode,
      entryId: state.selectedEntryId,
      response: state.response,
      fieldLabel: label,
      newDesignLabel: newDesignLabel,
      designMatch: state.designMatch,
      summary: fallbackSummary,
      previewUrl: state.previewUrl || (restore && restore.previewUrl ? restore.previewUrl : ''),
      productionUrl: state.productionUrl || (restore && restore.productionUrl ? restore.productionUrl : '')
    });
    Object.keys(props).forEach(function (key) {
      selections[key] = props[key];
    });
  }

  function getActiveContext() {
    var PB = global.productBuilder;
    return PB && typeof PB.getActiveContext === 'function' ? PB.getActiveContext() : null;
  }

  function getContainerParts() {
    var s = global.builderState;
    if (!s) return null;
    var isMain = s.mode === 'MAIN';
    return {
      containerID: isMain ? '#main-flow-container' : '#sub-flow-container',
      prefix: isMain ? '' : 'sub-'
    };
  }

  function getContentStep(stepNum) {
    var parts = getContainerParts();
    if (!parts) return null;
    return document.querySelector(parts.containerID + ' #' + parts.prefix + 'content-page-' + stepNum);
  }

  function getImageStep(stepNum) {
    var parts = getContainerParts();
    if (!parts) return null;
    return document.querySelector(parts.containerID + ' #' + parts.prefix + 'img-page-' + stepNum);
  }

  function setWrapUi(wrap, patch) {
    if (!wrap) return;
    var loading = wrap.querySelector('.pb-dl-loading');
    var gridWrap = wrap.querySelector('.pb-dl-grid-wrap');
    var grid = wrap.querySelector('.pb-dl-grid');
    var scratchActions = wrap.querySelector('.pb-dl-actions');
    var scratch = wrap.querySelector('.pb-dl-scratch');
    var required = wrap.querySelector('.pb-dl-required');

    if (patch.loading != null && loading) loading.style.display = patch.loading ? 'flex' : 'none';
    if (patch.gridVisible != null && gridWrap) gridWrap.style.display = patch.gridVisible ? 'block' : 'none';
    if (patch.scratchVisible != null) {
      if (scratchActions) scratchActions.style.display = patch.scratchVisible ? 'flex' : 'none';
      else if (scratch) scratch.style.display = patch.scratchVisible ? 'inline-flex' : 'none';
    }
    if (patch.newDesignLabel && scratch) scratch.textContent = patch.newDesignLabel;
    if (patch.requiredValid != null && required) {
      required.dataset.valid = patch.requiredValid ? 'true' : 'false';
      required.value = patch.requiredValid ? 'selected' : '';
    }
    if (patch.gridHtml != null && grid) {
      grid.innerHTML = patch.gridHtml;
      grid.querySelectorAll('.pb-design-card--catalog img').forEach(function (img) {
        global.PBStoreMedia?.mountStoreMedia(img, { width: 100, height: 100, compact: true });
      });
    }
  }

  function syncPreviewOnlyChrome(wrap, previewOnly) {
    var stepEl = wrap && wrap.closest('.pb-step');
    if (!stepEl) return;
    stepEl.classList.toggle('pb-dl-preview-only', !!previewOnly);
    stepEl.classList.toggle('pb-step-hide-question', !!previewOnly);
  }

  function getBuilderFieldMeta(label) {
    var data = global.BUILDER_DATA || {};
    var main = data.mainProduct && Array.isArray(data.mainProduct.personalization)
      ? data.mainProduct.personalization
      : [];
    for (var i = 0; i < main.length; i++) {
      if (main[i] && main[i].label === label) return main[i];
    }
    return null;
  }

  function getScratchPreviewCopy(wrap) {
    var settings = parseDesignLibrarySettings
      ? parseDesignLibrarySettings(wrap && wrap.dataset ? wrap.dataset.settings || '' : '')
      : {};
    var label = wrap && wrap.dataset ? wrap.dataset.pbDlLabel || '' : '';
    var fieldMeta = label ? getBuilderFieldMeta(label) : null;
    if (resolveNewDesignPreviewCopy) return resolveNewDesignPreviewCopy(settings, fieldMeta);
    return {
      preview: settings.new_design_preview || DEFAULT_NEW_DESIGN_PREVIEW,
      msg: settings.new_design_preview_message || (fieldMeta && fieldMeta.description) || DEFAULT_NEW_DESIGN_PREVIEW_MSG
    };
  }

  function applyScratchPreviewCopy(wrap, imgStep) {
    if (!wrap || !imgStep) return;
    var copy = getScratchPreviewCopy(wrap);
    var card = imgStep.querySelector('.pb-dl-preview-scratch-card');
    var msg = imgStep.querySelector('.pb-dl-preview-scratch-msg');
    if (card) card.textContent = copy.preview;
    if (msg) msg.textContent = copy.msg;
  }

  function setPreviewStageMode(imgStep, mode) {
    if (!imgStep) return;
    var stage = imgStep.querySelector('.pb-dl-preview-stage') || imgStep;
    stage.classList.toggle('is-scratch-preview', mode === 'new_design');
  }

  function updatePreviewForWrap(wrap, previewUrl, label, options) {
    options = options || {};
    var parts = getContainerParts();
    if (!parts || !wrap) return;
    var stepEl = wrap.closest('.pb-step');
    if (!stepEl || !stepEl.id) return;
    var m = stepEl.id.match(/content-page-(\d+)$/);
    if (!m) return;
    var imgStep = getImageStep(parseInt(m[1], 10));
    if (!imgStep) return;
    var empty = imgStep.querySelector('.pb-dl-preview-empty');
    var img = imgStep.querySelector('.pb-dl-preview-img');
    var scratchPreview = imgStep.querySelector('.pb-dl-preview-scratch');
    var media = img && global.PBStoreMedia?.mountStoreMedia(img);
    if (media) media.hidden = !!options.scratch || !previewUrl;
    if (options.scratch) {
      if (img) img.style.display = 'none';
      if (empty) empty.style.display = 'none';
      applyScratchPreviewCopy(wrap, imgStep);
      setPreviewStageMode(imgStep, 'new_design');
      return;
    }
    setPreviewStageMode(imgStep, previewUrl ? 'catalog' : 'empty');
    if (scratchPreview) scratchPreview.style.display = '';
    if (previewUrl && img) {
      if (media) global.PBStoreMedia.syncStoreMedia(media, previewUrl);
      else img.src = previewUrl;
      img.style.display = 'block';
      if (empty) empty.style.display = 'none';
    } else {
      if (img) img.style.display = 'none';
      if (empty) empty.style.display = 'block';
    }
  }

  function setScratchSelected(wrap, selected) {
    var scratch = wrap && wrap.querySelector('.pb-design-card--scratch');
    if (!scratch) return;
    scratch.classList.toggle('selected', !!selected);
    scratch.setAttribute('aria-pressed', selected ? 'true' : 'false');
  }

  function clearGridSelection(wrap) {
    var grid = wrap && wrap.querySelector('.pb-dl-grid');
    if (!grid) return;
    grid.querySelectorAll('.pb-design-card').forEach(function (el) { el.classList.remove('selected'); });
  }

  function dispatchDesignLibrarySaved() {
    try {
      document.dispatchEvent(new CustomEvent('pb-design-library-saved', { bubbles: true }));
    } catch (e) {}
  }

  function selectCatalogCard(wrap, card, label) {
    if (!wrap || !card || !label) return;
    var grid = wrap.querySelector('.pb-dl-grid');
    if (grid) {
      grid.querySelectorAll('.pb-design-card').forEach(function (el) { el.classList.remove('selected'); });
      card.classList.add('selected');
    }
    var state = getFieldState(label);
    var summary = summaryFromCatalogPick(state, card);
    state.selectedEntryId = (summary && summary.entryId) || card.dataset.entryId || null;
    state.selectionMode = 'catalog';
    state.selectionOrigin = null;
    commitCatalogUrlsToFieldState(state, summary);
    setScratchSelected(wrap, false);
    setWrapUi(wrap, { requiredValid: true });
    updatePreviewForWrap(
      wrap,
      (summary && summary.previewUrl) || card.dataset.previewUrl || '',
      (summary && summary.label) || card.dataset.label || ''
    );
    syncCartPropsForLabel(label);
    dispatchDesignLibrarySaved();
  }

  function selectScratchCard(wrap, label, newDesignLabel, options) {
    if (!wrap || !label) return;
    clearGridSelection(wrap);
    setScratchSelected(wrap, true);
    var state = getFieldState(label);
    state.selectedEntryId = null;
    state.selectionMode = 'new_design';
    /* MNGSD-2 — Original Design by the shopper's hand is a choice; by the fallback it is not,
       and a later shape that has a design takes it back (D2 'recovered'). */
    state.selectionOrigin = options && options.origin === 'fallback' ? 'fallback' : 'chosen';
    state.previewUrl = '';
    state.productionUrl = '';
    if (state.pendingCartRestore && state.pendingCartRestore.savedLabelText) {
      state.restoredCartLabel = String(state.pendingCartRestore.savedLabelText).trim();
      newDesignLabel = state.restoredCartLabel;
    } else if (!state.cartEditDepsChanged) {
      state.restoredCartLabel = null;
    }
    setWrapUi(wrap, { requiredValid: true });
    updatePreviewForWrap(wrap, '', newDesignLabel || DEFAULT_NEW_DESIGN_LABEL, { scratch: true });
    syncCartPropsForLabel(label);
    dispatchDesignLibrarySaved();
  }

  function applyDefaultSelectionAfterShow(wrap, label, summaries, settings, response) {
    var newDesignLabel = settings.new_design_label || DEFAULT_NEW_DESIGN_LABEL;
    if (isScratchOnlyResponse && isScratchOnlyResponse(response)) {
      var state = getFieldState(label);
      if (state.pendingCartRestore && state.pendingCartRestore.savedLabelText && !state.cartEditDepsChanged) {
        newDesignLabel = resolveNewDesignLabelForRestore
          ? resolveNewDesignLabelForRestore(state.pendingCartRestore, settings)
          : state.pendingCartRestore.savedLabelText;
      }
      selectScratchCard(wrap, label, newDesignLabel);
      return;
    }
    maybeApplyCartEditRestore(wrap, label);
    autoSelectFirstCatalogMatch(wrap, label, summaries);
  }

  function autoSelectFirstCatalogMatch(wrap, label, summaries) {
    var state = getFieldState(label);
    if (state.pendingCartRestore) return;
    if (state.selectionMode === 'catalog' || state.selectionMode === 'new_design') return;
    var first = getFirstMatchSummary ? getFirstMatchSummary(summaries) : (summaries && summaries[0]);
    if (!first) return;
    var grid = wrap.querySelector('.pb-dl-grid');
    if (!grid) return;
    var card = first.entryId
      ? grid.querySelector('.pb-design-card--catalog[data-entry-id="' + first.entryId + '"]')
      : null;
    if (!card) card = grid.querySelector('.pb-design-card--catalog');
    if (!card) return;
    selectCatalogCard(wrap, card, label);
  }

  /**
   * MNGOPT-2 / grill D1 — tapping a design from another shape moves the shopper to that shape and
   * keeps the design.
   *
   * The shape is a personalization field, so the honest way to change it is to click its own
   * button: the builder then runs its own validity, template and re-lookup handlers instead of us
   * duplicating them. That re-lookup would normally clear the design selection (PB-DL locked
   * decision 10) — `pendingShapeSwitch` is the carve-out, and it is keyed on this tap
   * rather than on the shape value, so a shopper who changes shape by hand still clears.
   */
  function switchToOtherShape(wrap, card, label, settings) {
    var entryId = card.getAttribute('data-entry-id') || '';
    var shape = card.getAttribute('data-shape') || '';
    if (!shape) return;
    var state = getFieldState(label);
    var button = findShapeOptionButton(settings, shape);
    if (!button) {
      /* No button to move them to — selecting the design would promise a shape we cannot set. */
      return;
    }
    /* MNGOPT-7 — remember WHICH lookup we are waiting past, not just the design.
       A render can happen between the tap and the new shape's result — the step re-activating,
       an unrelated selection event — and it carries the OLD response. Consuming the pending
       design there threw it away before its own designs had arrived, and the shopper landed on
       a shape with nothing selected and an empty preview. */
    state.pendingShapeSwitch = { entryId: entryId, fromKey: state.orderPropsKey || '' };
    /* MNGOPT-5 — select FIRST, so the tap previews the design immediately, exactly like picking
       one for their own shape. The shape change below re-looks-up; the pending switch re-selects
       the same design when that lands. */
    selectCatalogCard(wrap, card, label);
    applyShapeSelection(button);
  }

  /**
   * MNGOPT-5 — set the shape field WITHOUT advancing the step.
   *
   * Clicking the button itself was the obvious move and the wrong one: the builder's own handler
   * ends in `moveStep(1)`, so a tap here threw the shopper onto the next page instead of showing
   * them the design they had just chosen. This mirrors that handler's single-select branch —
   * active class, selection, validity, templates — and stops short of the navigation.
   */
  function applyShapeSelection(button) {
    var PB = global.productBuilder;
    if (!PB || typeof PB.getActiveContext !== 'function') return false;
    var ctx = PB.getActiveContext();
    if (!ctx || !ctx.selections) return false;
    var grid = (button.closest && button.closest('.btn-grid')) || button.parentElement;
    if (grid) {
      grid.querySelectorAll('.builder-btn').forEach(function (b) { b.classList.remove('active'); });
    }
    button.classList.add('active');
    ctx.selections[button.dataset.label] = button.dataset.value;
    /* checkButtonValidity is what tells the Design Library its inputs moved, so the re-lookup
       for the new shape rides on it — the same way a shopper's own shape change does. */
    if (grid && typeof PB.checkButtonValidity === 'function') PB.checkButtonValidity(grid);
    if (typeof PB.syncPersonalizationTemplates === 'function') {
      PB.syncPersonalizationTemplates(
        global.builderState && global.builderState.mode === 'MAIN'
          ? '#main-flow-container'
          : '#sub-flow-container'
      );
    }
    return true;
  }

  /** The shape field's own option button for `shape`, matched case-insensitively. */
  function findShapeOptionButton(settings, shape) {
    var fieldLabel = (settings && settings.shape_field) || 'Shape';
    var target = String(shape).trim().toLowerCase();
    var found = null;
    ['#main-flow-container', '#sub-flow-container'].forEach(function (selector) {
      if (found) return;
      var root = document.querySelector(selector);
      if (!root) return;
      root.querySelectorAll('.btn-grid').forEach(function (grid) {
        if (found) return;
        var input = grid.parentElement && grid.parentElement.querySelector('[data-label]');
        var gridLabel = grid.getAttribute('data-label') || (input && input.dataset.label) || '';
        if (String(gridLabel).trim().toLowerCase() !== fieldLabel.trim().toLowerCase()) return;
        grid.querySelectorAll('.builder-btn').forEach(function (btn) {
          if (found) return;
          /* MNGOPT-6 — the field's option can carry a note the catalog value does not, e.g.
             `Diamond (Popular)` vs `diamond`. Exact matching made those cards inert. */
          if (shapeMatchesOptionValue
            ? shapeMatchesOptionValue(target, btn.dataset.value)
            : String(btn.dataset.value || '').trim().toLowerCase() === target) {
            found = btn;
          }
        });
      });
    });
    return found;
  }

  function bindWrapInteractions(wrap) {
    if (!wrap || wrap.dataset.pbDlBound === 'true') return;
    wrap.dataset.pbDlBound = 'true';
    var grid = wrap.querySelector('.pb-dl-grid');
    if (grid) {
      grid.addEventListener('click', function (e) {
        var card = e.target.closest('.pb-design-card');
        if (!card || !wrap.contains(card)) return;
        var label = wrap.dataset.pbDlLabel || '';
        var wrapSettings = parseDesignLibrarySettings
          ? parseDesignLibrarySettings(wrap.dataset.settings || '')
          : {};
        if (card.classList.contains('pb-design-card--other-shape')) {
          switchToOtherShape(wrap, card, label, wrapSettings);
          return;
        }
        if (card.classList.contains('pb-design-card--scratch')) {
          selectScratchCard(wrap, label, wrapSettings.new_design_label || DEFAULT_NEW_DESIGN_LABEL);
        } else {
          selectCatalogCard(wrap, card, label);
        }
        var PB = global.productBuilder;
        if (PB && typeof PB.checkStepValidity === 'function') PB.checkStepValidity();
      });
    }
  }

  function shouldPreserveSelection(state) {
    return !!(state && (state.selectionMode === 'catalog' || state.selectionMode === 'new_design'));
  }

  function getPersistedSelectionSnapshot(wrap) {
    var ctx = getActiveContext();
    if (!ctx || !parseDesignLibraryCartSnapshot) return null;
    var snapshot = parseCartSnapshotFromProps(ctx.selections, wrap);
    if (!snapshot || !snapshot.mode) return null;
    return snapshot;
  }

  function syncStateFromSelections(label, wrap) {
    var state = getFieldState(label);
    if (state.cartEditDepsChanged) return false;
    if (state.selectionMode === 'catalog' || state.selectionMode === 'new_design') {
      if (state.response && decideStepVisibility && decideStepVisibility(state.response) === 'show') {
        state.shown = true;
        state.prefetchedReady = false;
        state.skipped = false;
      }
      return true;
    }
    if (!wrap && typeof document !== 'undefined') {
      wrap = document.querySelector('.pb-dl-wrap[data-pb-dl-label="' + label + '"]');
    }
    var snapshot = getPersistedSelectionSnapshot(wrap);
    if (!snapshot || !snapshot.mode) return false;
    state.selectionMode = snapshot.mode;
    state.selectedEntryId = snapshot.mode === 'catalog' ? snapshot.entryId : null;
    if (!state.pendingCartRestore && snapshot.mode) {
      state.pendingCartRestore = {
        mode: snapshot.mode,
        entryId: snapshot.entryId || null,
        previewUrl: snapshot.previewUrl || '',
        productionUrl: snapshot.productionUrl || '',
        designMatch: snapshot.designMatch || ''
      };
    }
    if (state.response && decideStepVisibility && decideStepVisibility(state.response) === 'show') {
      state.shown = true;
      state.prefetchedReady = false;
      state.skipped = false;
    }
    return true;
  }

  function finishActivateWrap(wrap) {
    /* Validation runs once from the caller (moveStep/updateUI/resume coordinator). */
  }

  function restoreSelectionUi(wrap, label, settings, state, summaries) {
    if (!wrap || !state) return;
    var newDesignLabel = settings.new_design_label || DEFAULT_NEW_DESIGN_LABEL;
    if (state.selectionMode === 'new_design') {
      /* MNGSD-2 D2 'recovered' — Original Design the fallback put there gives way to a design
         on this shape; Original Design the shopper chose stays. */
      var recovered = state.selectionOrigin === 'fallback' && resolveSelectionForShape
        ? resolveSelectionForShape({ mode: 'new_design', origin: 'fallback' }, summaries)
        : null;
      var recoveredCard = recovered && recovered.outcome === 'recovered'
        ? wrap.querySelector('.pb-dl-grid .pb-design-card--catalog:not(.pb-design-card--other-shape)[data-entry-id="' + recovered.entryId + '"]')
        : null;
      if (recoveredCard) {
        selectCatalogCard(wrap, recoveredCard, label);
        return;
      }
      selectScratchCard(wrap, label, newDesignLabel, { origin: state.selectionOrigin });
      return;
    }
    if (state.selectionMode === 'catalog' && (state.selectedEntryId || state.pendingCartRestore)) {
      var grid = wrap.querySelector('.pb-dl-grid');
      var index = (global.BUILDER_DATA && global.BUILDER_DATA.designLibraryIndex) || [];
      /* MNGSD-2 — a tap on an other-shape design is the freshest intent there is; it wins over
         the saved cart line, which used to overwrite it here before the design was selected. */
      var tapped = state.pendingShapeSwitch && state.pendingShapeSwitch.entryId
        ? String(state.pendingShapeSwitch.entryId)
        : '';
      var restoreSnap = tapped ? null : (state.pendingCartRestore || getPersistedSelectionSnapshot(wrap));
      var resolved = restoreSnap && resolveCatalogEntryFromSnapshot
        ? resolveCatalogEntryFromSnapshot(restoreSnap, state.response, index)
        : null;
      if (tapped) state.selectedEntryId = tapped;
      else if (resolved && resolved.entryId) state.selectedEntryId = resolved.entryId;

      /* MNGSD-2 D2 — follow, or fall back. `summaries` are this shape's designs. The held design
         used to be found by id even when it was only in the grid as an OTHER-shape card, so a
         Round pick rode through a switch to Hexagon untouched (PP40377). Now a design the reply
         places on another shape is replaced by this shape's newest, or by Original Design; a
         design the reply cannot place is kept. Never empty. */
      if (resolveSelectionForShape && settings.show_other_shapes === true) {
        var next = resolveSelectionForShape(
          { mode: 'catalog', entryId: state.selectedEntryId },
          summaries,
          {
            heldShape: knownDesignShape ? knownDesignShape(state.response, state.selectedEntryId) : '',
            chosenShape: currentChosenShape(settings)
          }
        );
        if (next.mode === 'new_design') {
          state.pendingCartRestore = null;
          selectScratchCard(wrap, label, newDesignLabel, { origin: 'fallback' });
          return;
        }
        if (next.entryId) state.selectedEntryId = next.entryId;
      }

      var card = grid && state.selectedEntryId
        ? grid.querySelector('.pb-design-card--catalog:not(.pb-design-card--other-shape)[data-entry-id="' + state.selectedEntryId + '"]')
        : null;
      if (card) {
        state.pendingCartRestore = null;
        selectCatalogCard(wrap, card, label);
        return;
      }
      if (grid && resolved && selectDesignCardInGrid) {
        var picked = selectDesignCardInGrid(grid, resolved, state.response);
        if (picked) {
          state.selectedEntryId = picked.getAttribute('data-entry-id') || state.selectedEntryId;
          state.pendingCartRestore = null;
          selectCatalogCard(wrap, picked, label);
          return;
        }
      }
      var summary = state.selectedEntryId
        ? findMatchSummary(state.response, state.selectedEntryId)
        : null;
      if (summary) {
        state.selectedEntryId = summary.entryId;
        state.pendingCartRestore = null;
        commitCatalogUrlsToFieldState(state, summary);
        updatePreviewForWrap(wrap, summary.previewUrl || '', summary.label || '');
        setWrapUi(wrap, { requiredValid: true });
        syncCartPropsForLabel(label);
        return;
      }
    }
    applyDefaultSelectionAfterShow(wrap, label, summaries, settings, state.response);
  }

  function applyStepDecision(wrap, label, decision, response, settings, options) {
    options = options || {};
    var silent = !!options.silent;
    var state = getFieldState(label);
    var preserveSelection = shouldPreserveCartEditSelectionOnLookup
      ? shouldPreserveCartEditSelectionOnLookup(settings, {
        preserveSelection: !!options.preserveSelection,
        cartEditDepsChanged: !!state.cartEditDepsChanged
      })
      : !!options.preserveSelection;
    if (state.cartEditDepsChanged && shouldAutoSwitchCartEditOnDepsChange && shouldAutoSwitchCartEditOnDepsChange(settings)) {
      state.pendingCartRestore = null;
      state.restoredCartLabel = null;
    }
    var priorMode = preserveSelection ? state.selectionMode : null;
    var priorEntryId = preserveSelection ? state.selectedEntryId : null;
    state.loading = false;
    state.response = response;

    if (decision === 'skip') {
      state.skipped = true;
      state.shown = false;
      state.prefetchedReady = false;
      state.selectionMode = null;
      state.selectedEntryId = null;
      setScratchSelected(wrap, false);
      syncCartPropsForLabel(label);
      if (!silent) {
        syncPreviewOnlyChrome(wrap, false);
        setWrapUi(wrap, { loading: false, gridVisible: false, scratchVisible: false, requiredValid: true });
        var PB = global.productBuilder;
        if (PB && typeof PB.checkStepValidity === 'function') PB.checkStepValidity();
        if (PB && typeof PB.queueStepNavigation === 'function') {
          PB.queueStepNavigation('dl', PB._pbLastMoveDirection || 1);
        } else if (PB && typeof PB.moveStep === 'function') {
          var direction = PB._pbLastMoveDirection || 1;
          requestAnimationFrame(function () {
            maybeSkipStep(direction);
          });
        }
      }
      return;
    }

    if (silent) {
      state.skipped = false;
      state.prefetchedReady = true;
      if (shouldPreserveSelection(state)) {
        return;
      }
      if (isScratchOnlyResponse && isScratchOnlyResponse(response)) {
        state.shown = false;
        state.selectionMode = 'new_design';
        state.selectedEntryId = null;
        state.previewUrl = '';
        state.productionUrl = '';
        syncCartPropsForLabel(label);
        return;
      }
      state.shown = false;
      state.selectionMode = null;
      state.selectedEntryId = null;
      return;
    }

    state.prefetchedReady = false;
    state.skipped = false;
    state.shown = true;
    if (!preserveSelection) {
      state.selectionMode = null;
      state.selectedEntryId = null;
      setScratchSelected(wrap, false);
      syncCartPropsForLabel(label);
    }
    /* ENTW-ONE — narrow to `max_designs` BEFORE the picker decisions, so the UI reasons
       about what is actually offered. priorEntryId is pinned so a saved cart line never
       loses the design the customer already chose. Unset setting = unchanged behaviour. */
    /* MNGOPT-6 — a design tapped from another shape must survive `max_designs` on the re-lookup.
       Without pinning it the cap kept only the newest design for the new shape, so the shopper
       was moved to their shape and then shown a DIFFERENT design than the one they tapped. */
    var pinnedEntryId = (state.pendingShapeSwitch && state.pendingShapeSwitch.entryId) || priorEntryId;
    /* MNGOPT-10 — one cached reply holds every shape's designs, so read it as the shape the
       shopper has NOW. This is what makes a shape change instant: no request, no spinner. */
    var forShape = reshapeResponseForShape
      ? reshapeResponseForShape(response, currentChosenShape(settings), settings)
      : response;
    var shownResponse = applyDesignLibraryDisplayCap
      ? applyDesignLibraryDisplayCap(forShape, settings, { keepEntryId: pinnedEntryId })
      : forShape;
    var summaries = shownResponse && Array.isArray(shownResponse.matchSummaries) ? shownResponse.matchSummaries : [];
    /* MNGOPT-2 — the alternatives on other shapes. Empty for every field that did not opt in. */
    var otherShapes = selectOtherShapeOptions ? selectOtherShapeOptions(shownResponse, settings) : [];
    state.otherShapes = otherShapes;
    var newDesignLabel = settings.new_design_label || DEFAULT_NEW_DESIGN_LABEL;
    var scratchOnly = isScratchOnlyResponse && isScratchOnlyResponse(shownResponse);
    var showNewDesign = shouldShowNewDesign ? shouldShowNewDesign(settings, shownResponse) : true;
    var showCatalogPicker = shouldShowCatalogPicker ? shouldShowCatalogPicker(settings, shownResponse) : true;
    /* MNGOPT-2 — amends PB-DL locked decision 7. The step used to vanish on zero matches; with
       designs waiting on another shape there is now something real to offer, so it stays. */
    if (otherShapes.length) showCatalogPicker = true;
    var previewOnly = !showCatalogPicker && summaries.length === 1 && !scratchOnly;
    var hasSelection = preserveSelection && (priorMode === 'catalog' || priorMode === 'new_design');
    syncPreviewOnlyChrome(wrap, previewOnly);
    setWrapUi(wrap, {
      loading: false,
      gridVisible: showCatalogPicker,
      scratchVisible: false,
      gridHtml: renderThumbnailGrid(summaries, {
        newDesignLabel: newDesignLabel,
        showNewDesign: showNewDesign,
        otherShapes: otherShapes
      }),
      requiredValid: hasSelection || scratchOnly || (!showCatalogPicker && summaries.length === 1)
    });
    bindWrapInteractions(wrap);
    /* MNGSD-2 — the tap on an other-shape design is honoured inside restoreSelectionUi (the
       tapped id wins over the saved line); the MNGOPT-7 "ripe" carve-out that used to live here
       never fired after MNGOPT-10 keyed the lookup on the name, and is gone. */
    if (preserveSelection && priorMode) {
      state.selectionMode = priorMode;
      state.selectedEntryId = priorEntryId;
      restoreSelectionUi(wrap, label, settings, state, summaries);
    } else {
      applyDefaultSelectionAfterShow(wrap, label, summaries, settings, forShape);
    }
    /* MNGOPT-7 — never leave the shopper looking at designs with an empty preview. Restoring a
       selection can find no matching card (the design it was holding is not in this shape's
       results), and silently selecting nothing is what made the step look broken. */
    ensureSomethingSelected(wrap, label, summaries);
    /* MNGSD-2 — the tap is no longer in flight. The shape button fires this render
       synchronously, so the first full render after a tap IS the new shape's: the tapped design
       either landed or was superseded by the field's own rules. Since MNGOPT-10 the lookup key
       never changes on a shape switch, so the "ripe" branch above never consumed this; leaving
       it set would keep D4 asleep for the field and let a stale tap hijack a later render. */
    state.pendingShapeSwitch = null;
    if (state.cartEditDepsChanged) state.cartEditDepsChanged = false;
    var PB2 = global.productBuilder;
    if (PB2 && typeof PB2.checkStepValidity === 'function') PB2.checkStepValidity();
  }

  /**
   * MNGOPT-7 — if designs are on offer, one of them is selected and previewed. Full stop.
   *
   * Every path into this step is supposed to end with a selection, but a restore that cannot find
   * its card returns quietly, and the shopper is left with a row of designs and a blank preview.
   * This is the backstop, not the main path: it only fires when nothing else selected anything.
   */
  function ensureSomethingSelected(wrap, label, summaries) {
    if (!wrap || !Array.isArray(summaries) || !summaries.length) return;
    var grid = wrap.querySelector('.pb-dl-grid');
    if (!grid) return;
    if (grid.querySelector('.pb-design-card.selected')) return;
    var state = getFieldState(label);
    /* "Create from scratch" is a real, deliberate choice — never override it with a design. */
    if (state.selectionMode === 'new_design') return;
    if (state.pendingCartRestore) return;
    /* MNGSD-2 — a held design the reply cannot place has no card to show, but it is still the
       design the shopper (or their saved line) chose, and its preview is on screen. Unknown is
       not wrong: it is kept, not swapped for whichever card happens to be newest. */
    if (state.selectionMode === 'catalog' && state.selectedEntryId && state.previewUrl) return;
    var card = grid.querySelector('.pb-design-card--catalog:not(.pb-design-card--other-shape)')
      || grid.querySelector('.pb-design-card--catalog');
    if (card) selectCatalogCard(wrap, card, label);
  }

  function isWrapActiveStep(wrap) {
    var stepEl = wrap && wrap.closest('.pb-step');
    return !!(stepEl && stepEl.classList.contains('active'));
  }

  function forEachDesignLibraryWrap(fn) {
    ['#main-flow-container', '#sub-flow-container'].forEach(function (selector) {
      var root = document.querySelector(selector);
      if (!root) return;
      root.querySelectorAll('.pb-dl-wrap').forEach(fn);
    });
  }

  function activateCurrentStepWraps() {
    var parts = getContainerParts();
    var ctx = getActiveContext();
    if (!parts || !ctx) return;
    onStepActivated(parts.containerID, parts.prefix, ctx.step);
  }
  function getDesignLibraryResolveOptions(state) {
    var data = global.BUILDER_DATA || {};
    var useMock = shouldUseMockDesignLibrary
      ? shouldUseMockDesignLibrary({ useMock: !!data.designLibraryUseMock })
      : !!data.designLibraryUseMock;
    if (!useMock && typeof global.location !== 'undefined' && global.location.search) {
      if (/[?&]pbDlMock=1(?:&|$)/.test(global.location.search)) useMock = true;
    }
    var options = {
      useMock: useMock,
      proxyPath: data.designLibraryProxyPath || logic.DEFAULT_PROXY_PATH || '/apps/pp-design-library/resolve',
      maxRetries: LOOKUP_MAX_RETRIES
    };
    if (state && state.abortController && state.abortController.signal) {
      options.signal = state.abortController.signal;
    }
    return options;
  }

  function runLookupForWrap(wrap, options) {
    options = options || {};
    var silent = !!options.silent;
    if (!parseDesignLibrarySettings || !buildOrderProps || !buildLookupRequest || !decideStepVisibility) {
      console.warn('[PB-DL] Missing pb-design-library-logic.js');
      return;
    }
    if (!resolveDesignLibrary && !mockResolveDesignLibrary) {
      console.warn('[PB-DL] Missing design library resolver');
      return;
    }

    var label = wrap.dataset.pbDlLabel || '';
    var settings = parseDesignLibrarySettings(wrap.dataset.settings || '');
    var ctx = getActiveContext();
    var productId = global.BUILDER_DATA && (global.BUILDER_DATA.personalizationSourceId || (global.BUILDER_DATA.mainProduct && global.BUILDER_DATA.mainProduct.id));
    var gate = buildOrderProps(settings.depends_on, ctx && ctx.selections, settings.default_order_props);
    var state = getFieldState(label);
    var active = isWrapActiveStep(wrap);

    if (!gate.complete) {
      state.loading = false;
      state.skipped = false;
      state.shown = false;
      state.prefetchedReady = false;
      if (active) {
        setWrapUi(wrap, { loading: false, gridVisible: false, scratchVisible: false, requiredValid: false });
      }
      return;
    }

    var nextKey = lookupIdentity(settings, gate.orderProps).key;
    if (state.orderPropsKey && state.orderPropsKey !== nextKey) {
      var autoSwitch = isCartEditSession() && shouldAutoSwitchCartEditOnDepsChange && shouldAutoSwitchCartEditOnDepsChange(settings);
      resetFieldState(label);
      state = getFieldState(label);
      if (autoSwitch) {
        state.cartEditDepsChanged = true;
        clearDesignSelectionFromContext(label);
      }
      if (active) {
        updatePreviewForWrap(wrap, '', '');
        setScratchSelected(wrap, false);
      }
    }
    state.orderPropsKey = nextKey;

    if (hasCachedLookupResult && hasCachedLookupResult(state, nextKey)) {
      if (active) {
        if (state.skipped) {
          applyStepDecision(wrap, label, 'skip', state.response, settings);
        } else if (state.prefetchedReady || state.shown) {
          applyStepDecision(wrap, label, decideStepVisibility(state.response), state.response, settings, {
            preserveSelection: shouldPreserveSelection(state)
          });
        }
      }
      return;
    }

    if (state.response && !state.loading && state.orderPropsKey === nextKey) {
      applyStepDecision(wrap, label, decideStepVisibility(state.response), state.response, settings, {
        silent: silent && !active,
        preserveSelection: active && shouldPreserveSelection(state)
      });
      return;
    }

    var generation = beginLookupGeneration ? beginLookupGeneration(state) : 0;
    state.loading = true;
    if (!silent) {
      setWrapUi(wrap, { loading: true, gridVisible: false, scratchVisible: false, requiredValid: false });
    }

    var productionPageLabels = resolveProductionPageLabels
      ? resolveProductionPageLabels(
          global.BUILDER_DATA && global.BUILDER_DATA.productionPageLabelsOverride,
          settings.production_page_labels
        )
      : (settings.production_page_labels || []);
    var request = buildLookupRequest(productId, gate.orderProps, settings.preferred_page_labels, productionPageLabels, {
      includeOtherShapes: settings.show_other_shapes === true
    });
    var resolveOptions = getDesignLibraryResolveOptions(state);
    var resolveFn = resolveDesignLibraryWithRetry || resolveDesignLibrary || mockResolveDesignLibrary;
    var lookupPromise = resolveFn(request, resolveOptions).then(function (response) {
      if (!document.body.contains(wrap)) return;
      if (isStaleLookupGeneration && isStaleLookupGeneration(state, generation)) return;
      if (response && response.error === 'aborted') return;
      var stillActive = isWrapActiveStep(wrap);
      applyStepDecision(wrap, label, decideStepVisibility(response), response, settings, { silent: silent && !stillActive });
    }).catch(function (err) {
      if (isStaleLookupGeneration && isStaleLookupGeneration(state, generation)) return;
      if (isExpectedLookupAbort && isExpectedLookupAbort(state, err)) return;
      if (err && (err.name === 'AbortError' || err.code === 20)) return;
      if (state.abortController && state.abortController.signal && state.abortController.signal.aborted) return;
      console.warn('[PB-DL] lookup failed', err);
    }).finally(function () {
      if (isStaleLookupGeneration && isStaleLookupGeneration(state, generation)) return;
      if (state.lookupPromise === lookupPromise) state.lookupPromise = null;
    });
    state.lookupPromise = lookupPromise;
  }

  function scheduleLookupForWrap(wrap, options) {
    options = options || {};
    var label = wrap.dataset.pbDlLabel || '';
    var state = getFieldState(label);
    if (state.debounceTimer) clearTimeout(state.debounceTimer);
    if (!options.silent && isWrapActiveStep(wrap)) {
      setWrapUi(wrap, { loading: true, gridVisible: false, scratchVisible: false, requiredValid: false });
    }
    state.debounceTimer = setTimeout(function () {
      state.debounceTimer = null;
      runLookupForWrap(wrap, options);
    }, DEBOUNCE_MS);
  }

  function applyLayoutClass(wrap, settings) {
    if (!wrap || !normalizeDesignLibraryLayout) return;
    var layout = normalizeDesignLibraryLayout(settings && settings.layout);
    wrap.classList.remove('pb-dl-layout-carousel', 'pb-dl-layout-grid-two-row-scroll');
    wrap.classList.add('pb-dl-layout-' + layout);
  }

  function ensureSplitLayoutForControls(wrap) {
    var parts = getContainerParts();
    if (!parts) return;
    var root = document.querySelector(parts.containerID);
    if (root) root.classList.remove('pb-layout-full');
  }

  function activateWrap(wrap) {
    if (!wrap) return;
    ensureSplitLayoutForControls(wrap);
    var label = wrap.dataset.pbDlLabel || '';
    var settings = parseDesignLibrarySettings
      ? parseDesignLibrarySettings(wrap.dataset.settings || '')
      : {};
    syncStateFromSelections(label, wrap);
    var ctx = getActiveContext();
    var gate = buildOrderProps && ctx
      ? buildOrderProps(settings.depends_on, ctx.selections, settings.default_order_props)
      : { complete: false };
    var state = getFieldState(label);
    var nextKey = gate.complete ? lookupIdentity(settings, gate.orderProps).key : '';
    var preserveSelection = shouldPreserveSelection(state) || !!getPersistedSelectionSnapshot(wrap);
    if (state.cartEditDepsChanged && shouldAutoSwitchCartEditOnDepsChange && shouldAutoSwitchCartEditOnDepsChange(settings)) {
      preserveSelection = false;
    }

    applyLayoutClass(wrap, settings);
    bindWrapInteractions(wrap);
    queueCartEditRestore(wrap);

    if (gate.complete && hasCachedLookupResult && hasCachedLookupResult(state, nextKey)) {
      if (state.skipped) {
        setWrapUi(wrap, { loading: false, gridVisible: false, scratchVisible: false, requiredValid: true });
        finishActivateWrap(wrap);
        return;
      }
      applyStepDecision(wrap, label, decideStepVisibility(state.response), state.response, settings, {
        preserveSelection: preserveSelection
      });
      finishActivateWrap(wrap);
      return;
    }

    if (gate.complete && state.response && state.orderPropsKey === nextKey && decideStepVisibility(state.response) === 'show') {
      applyStepDecision(wrap, label, 'show', state.response, settings, {
        preserveSelection: preserveSelection
      });
      finishActivateWrap(wrap);
      return;
    }

    if (gate.complete && state.response && state.orderPropsKey === nextKey && preserveSelection) {
      applyStepDecision(wrap, label, decideStepVisibility(state.response), state.response, settings, {
        preserveSelection: true
      });
      finishActivateWrap(wrap);
      return;
    }

    if (state.loading) {
      setWrapUi(wrap, { loading: true, gridVisible: false, scratchVisible: false, requiredValid: false });
      finishActivateWrap(wrap);
      return;
    }

    if (!gate.complete) {
      setWrapUi(wrap, { loading: false, gridVisible: false, scratchVisible: false, requiredValid: false });
      finishActivateWrap(wrap);
      return;
    }

    scheduleLookupForWrap(wrap, { silent: false });
    finishActivateWrap(wrap);
  }

  function initWrap(wrap, options) {
    options = options || {};
    if (!wrap) return;
    if (options.force) {
      wrap.removeAttribute('data-pb-dl-bound');
      resetFieldState(wrap.dataset.pbDlLabel || '');
    }
    if (parseDesignLibrarySettings) {
      applyLayoutClass(wrap, parseDesignLibrarySettings(wrap.dataset.settings || ''));
    }
    bindWrapInteractions(wrap);
    queueCartEditRestore(wrap);

    if (isWrapActiveStep(wrap)) {
      activateWrap(wrap);
      return;
    }

    var label = wrap.dataset.pbDlLabel || '';
    var settings = parseDesignLibrarySettings(wrap.dataset.settings || '');
    var ctx = getActiveContext();
    var gate = buildOrderProps(settings.depends_on, ctx && ctx.selections, settings.default_order_props);
    var state = getFieldState(label);
    var nextKey = gate.complete ? lookupIdentity(settings, gate.orderProps).key : '';
    var shouldPrefetch = shouldScheduleDesignLibraryPrefetch
      ? shouldScheduleDesignLibraryPrefetch(state, gate, nextKey)
      : gate.complete && !(hasCachedLookupResult && hasCachedLookupResult(state, nextKey));

    if (shouldPrefetch) {
      scheduleLookupForWrap(wrap, { silent: true });
    }
  }

  function init(selector, options) {
    options = options || {};
    var root = document.querySelector(selector) || document;
    root.querySelectorAll('.pb-dl-wrap').forEach(function (wrap) {
      initWrap(wrap, options);
    });
  }

  function onStepActivated(containerSelector, prefix, stepNum) {
    var stepPage = document.querySelector(containerSelector + ' #' + prefix + 'content-page-' + stepNum);
    if (!stepPage) return;
    var wrap = stepPage.querySelector('.pb-dl-wrap');
    if (!wrap) return;
    activateWrap(wrap);
  }

  var _skipping = false;

  function maybeSkipStep(direction) {
    if (_skipping) return false;
    var ctx = getActiveContext();
    if (!ctx) return false;
    var stepPage = getContentStep(ctx.step);
    if (!stepPage) return false;
    var wrap = stepPage.querySelector('.pb-dl-wrap');
    if (!wrap) return false;
    var label = wrap.dataset.pbDlLabel || '';
    var state = getFieldState(label);
    if (!state.skipped) return false;
    var PB = global.productBuilder;
    if (!PB || typeof PB.moveStep !== 'function') return false;
    _skipping = true;
    try {
      PB.moveStep(direction === -1 ? -1 : 1);
    } finally {
      _skipping = false;
    }
    return true;
  }

  function validateStep(stepPage) {
    var wrap = stepPage.querySelector('.pb-dl-wrap');
    if (!wrap) return true;
    var label = wrap.dataset.pbDlLabel || '';
    syncStateFromSelections(label, wrap);
    var state = getFieldState(label);
    var persisted = !!getPersistedSelectionSnapshot(wrap);
    if (isDesignLibraryStepComplete) {
      if (isDesignLibraryStepComplete(state, { persistedPick: persisted })) return true;
    }
    if (state.skipped) return true;
    if (state.loading) return false;
    if (!state.shown) return false;
    var required = wrap.querySelector('.pb-dl-required');
    if (!required || required.dataset.required !== 'true') return true;
    return required.dataset.valid === 'true';
  }

  var _resumeCoalesceTimer = null;
  var _resumeRetryTimer = null;

  function resumeBuilderSession() {
    flushCartPropsToSelections();
    forEachDesignLibraryWrap(function (wrap) {
      ensureSplitLayoutForControls(wrap);
      var label = wrap.dataset.pbDlLabel || '';
      syncStateFromSelections(label, wrap);
      var state = getFieldState(label);
      if (state.pendingCartRestore && state.pendingCartRestore.mode) {
        /* MNGSD-2 — consume the restore BEFORE applying it, as maybeApplyCartEditRestore does,
           so the settle inside the restore is not skipped and the screen never shows the saved
           wrong-shape design between resume and the next flush. */
        var pendingRestore = state.pendingCartRestore;
        state.pendingCartRestore = null;
        restoreCartEditSelection(wrap, label, pendingRestore);
      } else if (shouldPreserveSelection(state) && !state.shown) {
        var snapshot = getPersistedSelectionSnapshot(wrap);
        if (snapshot && snapshot.mode) {
          restoreCartEditSelection(wrap, label, snapshot);
        }
      }
      if (isWrapActiveStep(wrap)) activateWrap(wrap);
    });
    var PB = global.productBuilder;
    if (PB && typeof PB.checkStepValidity === 'function') PB.checkStepValidity();
  }

  function scheduleResumeBuilderSession() {
    if (_resumeCoalesceTimer) clearTimeout(_resumeCoalesceTimer);
    _resumeCoalesceTimer = setTimeout(function () {
      _resumeCoalesceTimer = null;
      resumeBuilderSession();
      if (!_resumeRetryTimer) {
        _resumeRetryTimer = setTimeout(function () {
          _resumeRetryTimer = null;
          resumeBuilderSession();
        }, 450);
      }
    }, 0);
  }

  function flushCatalogStateForWrap(wrap) {
    var label = wrap.dataset.pbDlLabel || '';
    if (!label) return;
    syncStateFromSelections(label);
    var state = getFieldState(label);
    if (state.selectionMode === 'catalog' || state.selectionMode === 'new_design') {
      /* MNGSD-2 — the registry flush feeds the encoder from this state, and the encoder
         back-fills hidden keys from the selections. Both must agree, so the last look runs here
         and the selections are rebuilt from the settled state — the old design's hidden keys
         can never ride onto the new line. */
      syncCartPropsForLabel(label, { final: true });
      return;
    }
    var scratch = wrap.querySelector('.pb-design-card--scratch.selected');
    if (scratch) {
      state.selectedEntryId = null;
      state.selectionMode = 'new_design';
      return;
    }
    var grid = wrap.querySelector('.pb-dl-grid');
    var card = grid && grid.querySelector('.pb-design-card.selected');
    if (!card || card.classList.contains('pb-design-card--scratch')) return;
    var summary = summaryFromCatalogPick(state, card);
    state.selectedEntryId = (summary && summary.entryId) || card.dataset.entryId || null;
    state.selectionMode = 'catalog';
    commitCatalogUrlsToFieldState(state, summary);
  }

  function flushCatalogStateForStep(stepEl) {
    if (!stepEl) return;
    stepEl.querySelectorAll('.pb-dl-wrap').forEach(function (wrap) {
      flushCatalogStateForWrap(wrap);
    });
  }

  function flushCartPropsToSelections() {
    document.querySelectorAll('.pb-dl-wrap').forEach(function (wrap) {
      syncCartPropsForLabel(wrap.dataset.pbDlLabel || '', { final: true });
    });
  }

  function onSelectionsChanged() {
    forEachDesignLibraryWrap(function (wrap) {
      if (!parseDesignLibrarySettings || !buildOrderProps || !orderPropsKey) return;
      var label = wrap.dataset.pbDlLabel || '';
      var settings = parseDesignLibrarySettings(wrap.dataset.settings || '');
      var ctx = getActiveContext();
      var gate = buildOrderProps(settings.depends_on, ctx && ctx.selections, settings.default_order_props);
      var state = getFieldState(label);
      var nextKey = gate.complete ? lookupIdentity(settings, gate.orderProps).key : '';
      var active = isWrapActiveStep(wrap);
      var shouldPrefetch = shouldScheduleDesignLibraryPrefetch
        ? shouldScheduleDesignLibraryPrefetch(state, gate, nextKey)
        : gate.complete && !(hasCachedLookupResult && hasCachedLookupResult(state, nextKey));
      var shouldSchedule = shouldScheduleDesignLibraryLookup
        ? shouldScheduleDesignLibraryLookup(state, gate, nextKey, active)
        : active && gate.complete && state.orderPropsKey && nextKey && state.orderPropsKey !== nextKey;

      /* MNGOPT-10 — the shape moved but the lookup that answers this step did not. Re-read the
         reply already in hand as the new shape: instant, and no request. This is the whole point
         of keying the cache on the name — without it the shape change would change nothing. */
      var nextShape = gate.complete ? lookupIdentity(settings, gate.orderProps).shape : '';
      if (
        settings.show_other_shapes === true &&
        state.orderPropsKey && nextKey && state.orderPropsKey === nextKey &&
        state.response && !state.loading &&
        String(state.chosenShape || '') !== String(nextShape || '')
      ) {
        state.chosenShape = nextShape;
        if (active) {
          applyStepDecision(wrap, label, decideStepVisibility(state.response), state.response, settings, {
            preserveSelection: shouldPreserveSelection(state)
          });
        }
        return;
      }
      state.chosenShape = nextShape;

      if (state.orderPropsKey && nextKey && state.orderPropsKey !== nextKey) {
        var autoSwitch = isCartEditSession() && shouldAutoSwitchCartEditOnDepsChange && shouldAutoSwitchCartEditOnDepsChange(settings);
        resetFieldState(label);
        state = getFieldState(label);
        if (autoSwitch) {
          state.cartEditDepsChanged = true;
          clearDesignSelectionFromContext(label);
        }
        if (active) {
          updatePreviewForWrap(wrap, '', '');
          setScratchSelected(wrap, false);
        }
      }

      if (shouldPrefetch && !active) {
        scheduleLookupForWrap(wrap, { silent: true });
      } else if (shouldSchedule) {
        scheduleLookupForWrap(wrap, { silent: false });
      }
    });
  }

  function hydrateCartEdit(selector) {
    var root = document.querySelector(selector) || document;
    root.querySelectorAll('.pb-dl-wrap').forEach(function (wrap) {
      queueCartEditRestore(wrap);
      var label = wrap.dataset.pbDlLabel || '';
      var state = getFieldState(label);
      if (state.shown && state.response) {
        maybeApplyCartEditRestore(wrap, label);
      } else if (!state.loading && !(hasCachedLookupResult && hasCachedLookupResult(state, state.orderPropsKey))) {
        initWrap(wrap);
      } else if (isWrapActiveStep(wrap)) {
        activateWrap(wrap);
      }
    });
  }

  var api = {
    init: init,
    initWrap: initWrap,
    activateWrap: activateWrap,
    onStepActivated: onStepActivated,
    activateCurrentStepWraps: activateCurrentStepWraps,
    maybeSkipStep: maybeSkipStep,
    validateStep: validateStep,
    onSelectionsChanged: onSelectionsChanged,
    flushCartPropsToSelections: flushCartPropsToSelections,
    flushCatalogStateForWrap: flushCatalogStateForWrap,
    flushCatalogStateForStep: flushCatalogStateForStep,
    hydrateCartEdit: hydrateCartEdit,
    restoreCartEditSelection: restoreCartEditSelection,
    resumeBuilderSession: resumeBuilderSession,
    scheduleResumeBuilderSession: scheduleResumeBuilderSession
  };

  global.PBDesignLibrary = api;

  function runModule() {
    if (!global.productBuilder || !global.PBDesignLibraryLogic) return false;
    if (global._pbDesignLibraryModuleLoaded) return true;
    global._pbDesignLibraryModuleLoaded = true;
    init('#main-flow-container');
    init('#sub-flow-container');
    activateCurrentStepWraps();
    return true;
  }

  if (typeof document !== 'undefined') {
    if (!runModule()) {
      document.addEventListener('DOMContentLoaded', runModule);
      document.addEventListener('builder:ready', runModule);
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
